import React, { useState, useEffect } from "react";
import axios from "axios";

const Step2Resume = ({ formData, setFormData, onBack, onNext }) => {
  const [fileError, setFileError] = useState("");
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);

  const apiUrl = process.env.REACT_APP_API_URL;

  const backButtonClass =
    "bg-gray-200 text-gray-800 px-6 py-2 rounded-lg font-semibold hover:bg-gray-200 transition shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-300";
  const nextButtonClass =
    "bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-blue-300";

  const validate = () => {
    const newErrors = {};

    if (!formData.country) newErrors.country = "Country is required";
    if (!formData.industry) newErrors.industry = "Industry is required";
    if (!formData.qualification)
      newErrors.qualification = "Position is required";
    if (!formData.resume_path)
      newErrors.resume_path = "Resume upload is required";
    if (fileError) newErrors.resume_path = fileError;

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const validTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // for .docx
      "text/plain", // .txt
      "image/jpeg", // .jpg, .jpeg
      "image/png", // .png
    ];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (file) {
      if (!validTypes.includes(file.type)) {
        setFileError("Invalid file type");
        setFormData({ ...formData, resume_path: null });
      } else if (file.size > maxSize) {
        setFileError("File size exceeds 2MB");
        setFormData({ ...formData, resume_path: null });
      } else {
        setFileError("");
        setFormData({ ...formData, resume_path: file });
      }
    } else {
      setFileError("");
      setFormData({ ...formData, resume_path: null });
    }
  };

  useEffect(() => {
    validate();
  }, [formData, fileError]);

  useEffect(() => {
    axios
      .get(`${apiUrl}/countries`)
      .then((response) => {
        setCountries(response.data);
      })
      .catch((error) => {
        console.error("Error fetching country list:", error);
      });
  }, []);

  const handleNextClick = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-green-50 rounded-2xl shadow-2xl border border-blue-100 p-8 max-w-3xl mx-auto space-y-6">
      {/* Country */}
      <div>
        <label
          htmlFor="country"
          className="block mb-1 font-semibold text-gray-800"
        >
          Country <span className="text-red-600">*</span>
        </label>
        <select
          id="country"
          aria-invalid={errors.country ? "true" : "false"}
          aria-describedby={errors.country ? "country-error" : undefined}
          className={`w-full border p-3 rounded-lg transition-colors duration-300 focus:outline-none focus:ring-2 ${
            errors.country
              ? "border-red-500 focus:ring-red-400"
              : "border-gray-300 focus:ring-blue-300"
          }`}
          value={formData?.country || ""}
          onChange={(e) =>
            setFormData({ ...formData, country: e.target.value })
          }
        >
          <option value="">Select Country</option>
          {countries.map((country) => (
            <option key={country.country} value={country.country}>
              {country.country}
            </option>
          ))}
        </select>

        {errors.country && (
          <p
            id="country-error"
            className="text-red-600 text-sm mt-1 font-medium select-none"
            role="alert"
          >
            {errors.country}
          </p>
        )}
      </div>

      {/* Industry */}
      <div>
        <label
          htmlFor="industry"
          className="block mb-1 font-semibold text-gray-800"
        >
          Industry <span className="text-red-600">*</span>
        </label>
        <select
          id="industry"
          aria-invalid={errors.industry ? "true" : "false"}
          aria-describedby={errors.industry ? "industry-error" : undefined}
          className={`w-full border p-3 rounded-lg transition-colors duration-300 focus:outline-none focus:ring-2 ${
            errors.industry
              ? "border-red-500 focus:ring-red-400"
              : "border-gray-300 focus:ring-blue-300"
          }`}
          value={formData?.industry || ""}
          onChange={(e) =>
            setFormData({ ...formData, industry: e.target.value })
          }
        >
          <option value="">Select Industry</option>
          <option value="Healthcare">Healthcare</option>
        </select>
        {errors.industry && (
          <p
            id="industry-error"
            className="text-red-600 text-sm mt-1 font-medium select-none"
            role="alert"
          >
            {errors.industry}
          </p>
        )}
      </div>

      {/* Qualification */}
      <div>
        <label
          htmlFor="qualification"
          className="block mb-1 font-semibold text-gray-800"
        >
          Position <span className="text-red-600">*</span>
        </label>
        <select
          id="qualification"
          aria-invalid={errors.qualification ? "true" : "false"}
          aria-describedby={
            errors.qualification ? "qualification-error" : undefined
          }
          className={`w-full border p-3 rounded-lg transition-colors duration-300 focus:outline-none focus:ring-2 ${
            errors.qualification
              ? "border-red-500 focus:ring-red-400"
              : "border-gray-300 focus:ring-blue-300"
          }`}
          value={formData?.qualification || ""}
          onChange={(e) =>
            setFormData({ ...formData, qualification: e.target.value })
          }
        >
          <option value="">Select Position</option>
          <option value="Assistant In Nursing">ASSISTANT IN NURSING</option>
          <option value="Personal Care Worker">PERSONAL CARE WORKER</option>
          <option value="Disability Support Worker">
            DISABILITY SUPPORT WORKER
          </option>
          <option value="Enrolled Nurse">ENROLLED NURSE</option>
          <option value="Registered Nurse">REGISTERED NURSE</option>
        </select>
        {errors.qualification && (
          <p
            id="qualification-error"
            className="text-red-600 text-sm mt-1 font-medium select-none"
            role="alert"
          >
            {errors.qualification}
          </p>
        )}
      </div>

      {/* Resume Upload */}
      <div>
        <label
          htmlFor="resume"
          className="block mb-1 font-semibold text-gray-800"
        >
          Upload Resume <span className="text-red-600">*</span>
        </label>
        <input
          id="resume"
          type="file"
          accept=".pdf,.docx,.txt,.jpg,.jpeg,.png"
          aria-invalid={errors.resume_path ? "true" : "false"}
          aria-describedby={errors.resume_path ? "resume-error" : undefined}
          className={`block w-full text-sm text-gray-600
             file:mr-4 file:py-2 file:px-4
             file:rounded-full file:border-0
             file:text-sm file:font-semibold
             file:bg-blue-50 file:text-blue-700
             hover:file:bg-blue-100
             focus:outline-none focus:ring-2 ${
               errors.resume_path ? "focus:ring-red-400" : "focus:ring-blue-300"
             } transition-colors duration-300`}
          onChange={handleFileChange}
        />
        {errors.resume_path && (
          <p
            id="resume-error"
            className="text-red-600 text-sm mt-1 font-medium select-none"
            role="alert"
          >
            {errors.resume_path}
          </p>
        )}
        <p className="text-sm text-gray-500 mt-1">
          Only .docx, .pdf, .txt, .jpg, .jpeg, .png file formats (Max 2MB)
        </p>
        {formData.resume_path && !errors.resume_path && (
          <p className="text-green-600 text-sm mt-1 select-text">
            Selected file: {formData.resume_path.name}
          </p>
        )}
      </div>

      {/* Buttons */}
      <div className="flex justify-between mt-8">
        <button className={backButtonClass} onClick={onBack} type="button">
          Back
        </button>
        <button
          className={nextButtonClass}
          onClick={handleNextClick}
          disabled={Object.keys(errors).length > 0}
          type="button"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Step2Resume;
